/**
 * OFC Game Actions
 *
 * State-transforming actions for OFC gameplay. Each action:
 * - Validates the action is legal
 * - Returns new immutable state
 * - Includes the operation that was applied (for logging/replay)
 *
 * Actions: place, dealHole, beginRound, advanceRound, endGame
 */
import { isBoardComplete, placePlacementsOnBoard } from './board';
import { ActingMode, RowPosition } from './enums';
import { HoleDealing, Operation, Placing, RoundAdvancing } from './operations';
import {
  addDiscard,
  addOperation,
  getBoard,
  getNextActor,
  isCardUsed,
  removeActor,
  setActorIndex,
  setActors,
  setBoard,
  setRoundIndex,
  setStatus,
} from './state';
import { Card, GameState, Placement } from './types';
import { getCardsToDiscard, getCardsToPlace, getVariantConfig } from './variants';

/** Result of an action: new state + optional operation + optional errors */
export interface ActionResult {
  readonly state: GameState;
  readonly operation?: Operation;
  readonly errors?: readonly string[];
}

/** Validation result for a placing action */
export interface PlacingValidation {
  readonly valid: boolean;
  readonly errors: readonly string[];
}

/** Validate a placing action without applying it */
export function verifyPlacing(
  state: GameState,
  playerIndex: number,
  placements: readonly Placement[],
  discard?: Card,
): PlacingValidation {
  const errors: string[] = [];

  if (!state.status) {
    errors.push('Game is complete');
    return { valid: false, errors };
  }

  if (!state.actors.has(playerIndex)) {
    errors.push(`Player ${playerIndex} cannot act`);
    return { valid: false, errors };
  }

  if (state.roundIndex === null) {
    errors.push('Round has not started');
    return { valid: false, errors };
  }

  const config = getVariantConfig(state.variant);

  // Fantasy players always place all 13 cards at once (and discard 1 from 14 dealt)
  // This can happen in round 0 (all players in fantasy) or after regular rounds complete
  const isFantasyPlayer = state.fantasyPlayers.has(playerIndex);
  const expectedPlacements = isFantasyPlayer ? 13 : getCardsToPlace(config, state.roundIndex);
  const expectedDiscards = isFantasyPlayer ? 1 : getCardsToDiscard(config, state.roundIndex);

  if (placements.length !== expectedPlacements) {
    errors.push(`Expected ${expectedPlacements} placements, got ${placements.length}`);
  }

  if (expectedDiscards > 0 && !discard) {
    errors.push('Discard is required');
  }

  if (expectedDiscards === 0 && discard) {
    errors.push('Discard is not allowed this round');
  }

  const board = getBoard(state, playerIndex);
  if (!board) {
    errors.push(`Board not found for player ${playerIndex}`);
    return { valid: false, errors };
  }

  const allCards = discard ? [...placements.map((p) => p.card), discard] : placements.map((p) => p.card);
  for (const card of allCards) {
    if (isCardUsed(state, card)) {
      errors.push(`Card ${card.rank}${card.suit} is already used`);
    }
  }

  const rowCounts = new Map<RowPosition, number>();
  for (const placement of placements) {
    const currentCount = rowCounts.get(placement.position) ?? 0;
    rowCounts.set(placement.position, currentCount + 1);
  }

  for (const [position, count] of rowCounts) {
    const row = board[position];
    const capacity = position === RowPosition.FRONT ? 3 : 5;
    const available = capacity - row.cards.length;
    if (count > available) {
      errors.push(`Cannot place ${count} cards in ${position}, only ${available} slots available`);
    }
  }

  return { valid: errors.length === 0, errors };
}

/** Check if a player can place cards */
export function canPlace(state: GameState, playerIndex: number): boolean {
  if (!state.status || state.roundIndex === null) {
    return false;
  }
  return state.actors.has(playerIndex);
}

/** Place cards on the board and optionally discard */
export function place(
  state: GameState,
  playerIndex: number,
  placements: readonly Placement[],
  discard?: Card,
): ActionResult {
  const validation = verifyPlacing(state, playerIndex, placements, discard);
  if (!validation.valid) {
    return { state, errors: validation.errors };
  }

  const operation: Placing = {
    type: 'placing',
    playerIndex,
    placements,
    discard,
  };

  let newState = state;
  const board = getBoard(newState, playerIndex)!;
  const newBoard = placePlacementsOnBoard(board, placements);

  newState = setBoard(newState, playerIndex, newBoard);

  if (discard) {
    newState = addDiscard(newState, discard);
  }

  newState = addOperation(newState, operation);
  newState = removeActor(newState, playerIndex);

  if (newState.actingMode === ActingMode.SEQUENTIAL) {
    newState = setActorIndex(newState, getNextActor(newState));
  }

  return { state: newState, operation };
}

/** Check if hole cards can be dealt to a player */
export function canDealHole(state: GameState, playerIndex: number): boolean {
  return state.status && state.actors.has(playerIndex);
}

/** Deal hole cards to a player (records operation, table machine handles actual cards) */
export function dealHole(state: GameState, playerIndex: number, cards: readonly Card[]): ActionResult {
  if (!state.status) {
    return { state, errors: ['Game is complete'] };
  }

  const operation: HoleDealing = {
    type: 'hole_dealing',
    playerIndex,
    cards,
  };

  const newState = addOperation(state, operation);
  return { state: newState, operation };
}

/** Check if a new round can begin (all players must have acted) */
export function canBeginRound(state: GameState): boolean {
  return state.status && state.actors.size === 0;
}

/** Begin a specific round (sets up actors, handles fantasy players) */
export function beginRound(state: GameState, roundIndex: number): ActionResult {
  if (!state.status) {
    return { state, errors: ['Game is complete'] };
  }

  const config = getVariantConfig(state.variant);

  // Check if ALL players are in Fantasy
  const allPlayersInFantasy = state.fantasyPlayers.size === state.playerCount && state.fantasyPlayers.size > 0;

  const actors = new Set<number>();
  for (let i = 0; i < state.playerCount; i++) {
    const board = getBoard(state, i);
    const boardComplete = board ? isBoardComplete(board) : true;
    const isFantasyPlayer = state.fantasyPlayers.has(i);

    if (board && !boardComplete) {
      if (!isFantasyPlayer) {
        // Non-Fantasy players act in rounds 0 through totalRounds
        if (roundIndex <= config.totalRounds) {
          actors.add(i);
        }
      } else {
        // Fantasy players act:
        // - In round 0 ONLY if ALL players are in Fantasy
        // - OR after totalRounds (when non-fantasy is done and fantasy takes their turn)
        if ((roundIndex === 0 && allPlayersInFantasy) || roundIndex > config.totalRounds) {
          actors.add(i);
        }
      }
    }
  }

  const operation: RoundAdvancing = {
    type: 'round_advancing',
    roundNumber: roundIndex,
  };

  let newState = setRoundIndex(state, roundIndex);
  newState = setActors(newState, actors);

  if (newState.actingMode === ActingMode.SEQUENTIAL && actors.size > 0) {
    const firstActor = Math.min(...Array.from(actors));
    newState = setActorIndex(newState, firstActor);
  }

  newState = addOperation(newState, operation);

  return { state: newState, operation };
}

/** Check if round can advance (all players must have acted) */
export function canAdvanceRound(state: GameState): boolean {
  return state.status && state.actors.size === 0;
}

/** Advance to the next round (or end game if all rounds complete) */
export function advanceRound(state: GameState): ActionResult {
  if (!state.status) {
    return { state, errors: ['Game is complete'] };
  }

  if (state.actors.size > 0) {
    return { state, errors: ['Not all players have acted'] };
  }

  const config = getVariantConfig(state.variant);
  const currentRound = state.roundIndex ?? -1;
  const nextRound = currentRound + 1;

  if (nextRound > config.totalRounds) {
    return endGame(state);
  }

  return beginRound(state, nextRound);
}

/** Check if game can be ended */
export function canEndGame(state: GameState): boolean {
  return state.status;
}

/** End the game (sets status to false, clears actors) */
export function endGame(state: GameState): ActionResult {
  if (!state.status) {
    return { state, errors: ['Game is already complete'] };
  }

  let newState = setStatus(state, false);
  newState = setActors(newState, new Set());
  newState = setActorIndex(newState, null);

  return { state: newState };
}
